import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from './firebase';

// Is the signed-in user a named admin of this module?
//
// Four modules used to keep their own list of "these specific people may also edit" inside their own
// settings document -- attendance_settings.permissions[category].editStaff, and
// {invigilation,patrol,quiz_tutorial}_settings.editPermissions.staff. That is the same idea as a
// module admin, built four more times, and none of it was enforced by the security rules: the
// checkboxes decided which buttons rendered and nothing else.
//
// They now all read metadata/module_admins, which is the one list the rules test. The role matrix in
// each module stays -- "all teachers may verify student attendance" is a real, useful setting and it
// has sensible defaults. What is gone is the per-action list of individual people, because that is
// exactly what naming a module admin does.
//
// Deliberately read-only here. metadata/module_admins is system-admin-write-only, and it has to stay
// that way: a module admin who could edit it would add themselves to every other module.
export function useModuleAdmin(schoolId, moduleId) {
  const [isModuleAdmin, setIsModuleAdmin] = useState(false);

  useEffect(() => {
    if (!schoolId || !moduleId) { setIsModuleAdmin(false); return undefined; }

    const email = (auth.currentUser?.email || '').trim().toLowerCase();
    if (!email) { setIsModuleAdmin(false); return undefined; }

    return onSnapshot(
      doc(db, 'schools', schoolId, 'metadata', 'module_admins'),
      (snap) => {
        const list = (snap.exists() ? snap.data()[moduleId] : null) || [];
        setIsModuleAdmin(list.map((e) => String(e).toLowerCase()).includes(email));
      },
      // A student or guardian cannot read this document. That is not an error worth surfacing --
      // they are not a module admin either way.
      () => setIsModuleAdmin(false),
    );
  }, [schoolId, moduleId]);

  return isModuleAdmin;
}
